import { createRouter, createWebHistory } from 'vue-router';
import { frontendURL } from '../helper/URLHelper';
import dashboard from './dashboard/dashboard.routes';
import store from 'dashboard/store';
import { validateLoggedInRoutes } from '../helper/routeHelpers';
import AnalyticsHelper from '../helper/AnalyticsHelper';
import { buildPermissionsFromRouter } from '../helper/permissionsHelper';
import { useAlert } from 'dashboard/composables';
import { createI18n } from 'vue-i18n';
import i18nMessages from 'dashboard/i18n';

const i18n = createI18n({
  legacy: false,
  locale: 'vi',
  messages: i18nMessages,
  fallbackLocale: 'en',
});

const routes = [...dashboard.routes];

export const router = createRouter({ 
  history: createWebHistory(), 
  routes 
});

export const routesWithPermissions = buildPermissionsFromRouter(routes);

const ensureAccountLoaded = async (accountId) => {
  if (!accountId) return;

  const account = store.getters['accounts/getAccount'](accountId);
  if (account?.features) return;

  await store.dispatch('accounts/get').catch(() => {
  });
};

const resolveRouteFeatureFlags = to => {
  const seen = new Set();
  const out = [];
  // Child routes first; then parents for fallbacks
  [...to.matched].reverse().forEach(r => {
    let raw = r.meta?.featureFlag;
    if (!raw) return;
    raw = typeof raw === 'function' ? raw(to) : raw;
    const flags = Array.isArray(raw) ? raw : [raw];
    flags.forEach(f => {
      if (f && !seen.has(f)) {
        seen.add(f);
        out.push(f);
      }
    });
  });
  return out;
};

const getFeatureAccess = (features, flags) => {
  const missing = flags.filter(f => features?.[f] !== true);
  return { allowed: missing.length === 0, missing };
};

export const validateAuthenticateRoutePermission = async (to, from, next) => {
  const { isLoggedIn, getCurrentUser: user } = store.getters;
  if (!isLoggedIn) { window.location.assign('/app/login'); return; }

  if (!to.name) { next(frontendURL(`accounts/${user.account_id}/dashboard`)); return; }

  const redirectRoute = validateLoggedInRoutes(to, user);
  if (redirectRoute) { next(frontendURL(redirectRoute)); return; }

  const accountId = Number(to.params.accountId);
  await ensureAccountLoaded(accountId);

  // Compute once
  const flags = resolveRouteFeatureFlags(to);
  if (flags.length === 0) { next(); return; }

  const account = store.getters['accounts/getAccount'](accountId);
  const { allowed, missing } = getFeatureAccess(account?.features, flags);

  if (!allowed) {
    sessionStorage.setItem('upgrade_for', missing[0]);
    next(frontendURL(`accounts/${accountId}/settings/billing`));
    return;
  }

  next();
};

export const initalizeRouter = () => {
  const userAuthentication = store.dispatch('setUser');

  router.beforeEach(async (to, from, next) => {
    AnalyticsHelper.page(to.name || '', {
      path: to.path,
      name: to.name,
    });

    await userAuthentication;
    await validateAuthenticateRoutePermission(to, from, next);
  });

  router.afterEach((to) => {
    if (to.name === 'billing_settings_index') {
      const upgradeFor = sessionStorage.getItem('upgrade_for');
      if (upgradeFor) {
        // Defer to ensure snackbar container is mounted
        setTimeout(() => {
          const t = i18n.global.t.bind(i18n.global);
          useAlert({
            type: 'alert',
            title: t('BILLING_SETTINGS.UPGRADE_REQUIRED.TITLE'),
            description: t('BILLING_SETTINGS.UPGRADE_REQUIRED.DESCRIPTION'),
          });
          sessionStorage.removeItem('upgrade_for');
        }, 300);
      }
    }
  });
};

export default router;